// Which Home cards an org's portal shows.
//
// Home is a dashboard of doorways: every card summarises one of the nav's
// primary destinations and links straight into it. Narrowing the nav in Portal
// settings therefore has to narrow Home too, or the destination an org just
// removed is offered right back from the dashboard (PLO-5580) — Catalog gone
// from the nav, but the catalogs grid, Frequent Access and the "Browse the
// catalog" call to action all still pointing at it.
//
// Takes the ids the nav actually renders (see `visibleNavIds`) rather than the
// stored selection, so the cards cannot disagree with the nav in the cases
// where it deliberately falls back to showing everything.
//
// Kept free of React so the mapping stays unit-testable on its own, matching
// `navVisibility`.

export interface HomeCardVisibility {
  /** My access donut. Doorway to My Access. */
  myAccess: boolean;
  /** Access Expiring Soon. Its rows route to My Access. */
  accessExpiring: boolean;
  /** Frequent Access. Re-requests through the catalog's request flow. */
  frequentAccess: boolean;
  /** Tasks queue. Doorway to Tasks. */
  tasks: boolean;
  /** Access review campaign queue. Doorway to Access Reviews. */
  accessReviews: boolean;
  /** Access Catalogs grid. Doorway to Catalog. */
  accessCatalogs: boolean;
  /** "Browse the catalog" action inside the My access empty state. */
  browseCatalog: boolean;
}

/**
 * Maps the nav's visible destinations onto Home's cards.
 *
 * Active Luna requests are deliberately absent: that card is not a nav
 * destination, and it already self-hides when there is nothing to answer.
 */
export const homeCardVisibility = (
  visibleNav: ReadonlySet<string>,
): HomeCardVisibility => {
  const access = visibleNav.has("access");
  const catalog = visibleNav.has("catalog");

  const cards = {
    myAccess: access,
    accessExpiring: access,
    frequentAccess: catalog,
    tasks: visibleNav.has("tasks"),
    accessReviews: visibleNav.has("reviews"),
    accessCatalogs: catalog,
  };

  return { ...cards, browseCatalog: catalog };
};

/**
 * What Home knows about the cards whose emptiness depends on data, not config.
 *
 * Every self-hiding card has to be represented here. A card that can render
 * nothing but is missing from this count makes the layout and the empty state
 * disagree with the page.
 */
export interface HomeCardData {
  /** Outstanding tasks. Tasks self-hides at zero. */
  taskCount: number;
  /** Luna requests still awaiting an answer. That card self-hides at zero. */
  activeLunaCount: number;
  /** Active review campaigns. The review queue self-hides at zero. */
  activeCampaignCount: number;
}

export interface HomeRails {
  left: boolean;
  right: boolean;
}

/**
 * Whether each column of Home's two-column grid has anything to put in it.
 *
 * Both rails can be emptied by the org's selection, so the two-column layout
 * has to be earned: a lone card stranded in the 401px track, or sitting beside
 * a blank 1fr, is the failure this prevents.
 *
 * My access, Access Expiring, Frequent Access and Access Catalogs render an
 * empty state rather than hiding, so enabling them settles their column
 * outright. Tasks, Luna and the review queue self-hide on empty data, so their
 * counts decide. Luna is not org-gated: it can put content in the right column
 * for an org that enabled nothing at all.
 */
export const homeRails = (
  cards: HomeCardVisibility,
  data: HomeCardData,
): HomeRails => ({
  left: cards.myAccess || cards.frequentAccess || cards.accessExpiring,
  right:
    cards.accessCatalogs ||
    (cards.accessReviews && data.activeCampaignCount > 0) ||
    (cards.tasks && data.taskCount > 0) ||
    data.activeLunaCount > 0,
});

/**
 * Whether Home renders no card at all, and so needs its own empty state.
 *
 * Asked of the rails rather than the org's selection: an org that enabled
 * nothing can still have an active Luna request, and an org that enabled the
 * review queue can still have no campaigns in it. Keying this on configuration
 * got both wrong, showing "nothing here" above real work in the first case and
 * a blank page in the second.
 */
export const homeIsEmpty = (rails: HomeRails): boolean =>
  !rails.left && !rails.right;
